import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { Identificable, LabelledEntity } from 'shared/types/commonView';
import type { WorkflowDecisionType } from 'shared/domain/workflow/types';
import { OperationV1 } from 'views/issue/issueHistory/types';

export enum DocumentUploadOperationType {
  documentUploadAttempt = 'documentUploadAttempt',
  documentUploadSuccess = 'documentUploadSuccess',
  documentUploadFailure = 'documentUploadFailure',
}

export enum DocumentDrawingOperationType {
  documentDrawingClear = 'documentDrawingClear',
  documentDrawingUploadAttempt = 'documentDrawingUploadAttempt',
  documentDrawingUploadSuccess = 'documentDrawingUploadSuccess',
  documentDrawingUploadFailure = 'documentDrawingUploadFailure',

  documentDrawingMergedSuccess = 'documentDrawingMergedSuccess',
  documentDrawingMergedFailure = 'documentDrawingMergedFailure',
  documentDrawingMergedPending = 'documentDrawingMergedPending',
  documentDrawingMergedUploadedUnavailable = 'documentDrawingMergedUploadedUnavailable',
}

export enum EventOperationType {
  eventCreation = 'eventCreation',
}

export enum UserAccessOperationType {
  userAccessCreation = 'userAccessCreation',
  userAccessModification = 'userAccessModification',
  userAccessDeletion = 'userAccessDeletion',
}

export enum DocumentDescriptionOperationType {
  documentDescriptionUpdate = 'documentDescriptionUpdate',
}

export enum DocumentExportToDocumentationOperationType {
  documentExportedToDocumentation = 'documentExportedToDocumentation',
}

export enum LinkedDocumentationOperationType {
  documentationImported = 'documentationImported',
  linkedDocumentationRemoved = 'linkedDocumentationRemoved',
}

export enum StageChangeOperationType {
  stageChange = 'stageChange',
  stageChangedAutomatically = 'stageChangedAutomatically',
}

export enum WorkflowDecisionOperationType {
  workflowDecisionMade = 'workflowDecisionMade',
}

/** The issue placed on a map version, or its placement confirmed on another one (PT-7117). */
export enum IssueLocationOperationType {
  issueLocalized = 'issueLocalized',
  issueLocationConfirmed = 'issueLocationConfirmed',
}

export type IssueChangelogOperationType =
  | DocumentUploadOperationType
  | DocumentDrawingOperationType
  | EventOperationType
  | UserAccessOperationType
  | DocumentDescriptionOperationType
  | DocumentExportToDocumentationOperationType
  | LinkedDocumentationOperationType
  | StageChangeOperationType
  | WorkflowDecisionOperationType
  | IssueLocationOperationType;

const KNOWN_OPERATION_TYPES: ReadonlySet<string> = new Set<string>([
  ...Object.values(DocumentUploadOperationType),
  ...Object.values(DocumentDrawingOperationType),
  ...Object.values(EventOperationType),
  ...Object.values(UserAccessOperationType),
  ...Object.values(DocumentDescriptionOperationType),
  ...Object.values(DocumentExportToDocumentationOperationType),
  ...Object.values(LinkedDocumentationOperationType),
  ...Object.values(StageChangeOperationType),
  ...Object.values(WorkflowDecisionOperationType),
  ...Object.values(IssueLocationOperationType),
]);

/** An operation the history can show; the backend may record newer ones (e.g. `versionDrawingAdded`, PT-7117) before the app can. */
export function isKnownIssueChangelogOperationType(operationType: unknown): operationType is IssueChangelogOperationType {
  return typeof operationType === 'string' && KNOWN_OPERATION_TYPES.has(operationType);
}

export enum IssueChangelogEntityType {
  issueDocument = 'issueDocument',
  issueDocumentDrawing = 'issueDocumentDrawing',
  eventDocument = 'eventDocument',
  eventDocumentDrawing = 'eventDocumentDrawing',
  event = 'event',
  userAccess = 'userAccess',
  linkedDocumentation = 'linkedDocumentation',
  stage = 'stage',
  workflowDecision = 'workflowDecision',
  location = 'location',
}

/** A change of the issue's location: placed on a map version, or confirmed on another one (PT-7117). */
export function isLocationChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return entityType === IssueChangelogEntityType.location;
}

export const eventOperationEntityTypes = Object.values([
  IssueChangelogEntityType.event,
  IssueChangelogEntityType.eventDocument,
  IssueChangelogEntityType.eventDocumentDrawing,
]);

export const issueOperationEntityTypes = Object.values([
  IssueChangelogEntityType.issueDocument,
  IssueChangelogEntityType.linkedDocumentation,
  IssueChangelogEntityType.issueDocumentDrawing,
]);

/** A change of an event of the issue: the event itself, its document or its drawing. */
export function isEventChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return eventOperationEntityTypes.includes(entityType);
}

/** A change of a file of the issue itself: its document, its drawing or a linked documentation. */
export function isIssueFileChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return issueOperationEntityTypes.includes(entityType);
}

export function isUserAccessChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return entityType === IssueChangelogEntityType.userAccess;
}

export function isStageChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return entityType === IssueChangelogEntityType.stage;
}

export function isWorkflowDecisionChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return entityType === IssueChangelogEntityType.workflowDecision;
}

export function isLinkedDocumentationChangelogEntity(entityType: IssueChangelogEntityType): boolean {
  return entityType === IssueChangelogEntityType.linkedDocumentation;
}

const LINKED_DOCUMENTATION_OPERATION_TYPES: ReadonlySet<IssueChangelogOperationType> = new Set(
  Object.values(LinkedDocumentationOperationType)
);

/** A documentation linked to the issue, or its link removed. */
export function isLinkedDocumentationOperation(operationType: IssueChangelogOperationType): boolean {
  return LINKED_DOCUMENTATION_OPERATION_TYPES.has(operationType);
}

/** A document of the issue exported as a documentation (version). */
export function isDocumentExportOperation(operationType: IssueChangelogOperationType): boolean {
  return operationType === DocumentExportToDocumentationOperationType.documentExportedToDocumentation;
}

export type OperationV2Dto = Identificable & {
  _id: string;
  createdBy: LabelledEntity | null;
  createdAt: string;
  entityType: IssueChangelogEntityType;
  operationType: IssueChangelogOperationType;
  // Final aggregated result for grouped upload operations sharing the same documentId
  finalOperationResult?: IssueChangelogOperationType;
  projectId: string;
  issueId: string;
  eventId: string | null;
  eventTitle: string | null;
  documentId: string | null;
  documentTitle: string | null;
  accessLevelAfter: AccessLevelModel | null;
  accessLevelBefore: AccessLevelModel | null;
  accessingUser: string | null;
  userAccessId: string | null;
  descriptionBefore: string | null;
  descriptionAfter: string | null;
  documentationId: string | null;
  documentationName: string | null;
  versionId: string | null;
  stageIdBefore: string | null;
  stageIdAfter: string | null;
  workflowDecisionType: WorkflowDecisionType | null;
  workflowDecisionStageId: string | null;
  version: 2;
};
export interface ChangelogOutDto {
  patchesV1: OperationV1[];
  patchesV2: OperationV2Dto[];
}
